/**
 * PalettePortal — Renders children in a fixed-position overlay anchored
 * above a reference element. Used for slash commands, mention, and link
 * search palettes that need to appear above the input bar.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PalettePortalProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function PalettePortal({ anchorRef, children }: PalettePortalProps) {
  const [pos, setPos] = useState<{ bottom: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef]);

  if (!pos) return null;
  return createPortal(
    <div style={{ position: 'fixed', bottom: pos.bottom, left: pos.left, width: pos.width, zIndex: 9999 }}>
      {children}
    </div>,
    document.body,
  );
}
